import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

interface ParaBirimi {
    paraBirimiID: number;
    birim: string;
    carpan: number;
}

interface ParaBirimiForm {
    birim: string;
    carpan: number;
}

// Currency code → symbol + color
const currencyMeta: Record<string, { symbol: string; bg: string; color: string }> = {
    TRY: { symbol: '₺', bg: '#fef3c7', color: '#b45309' },
    TL:  { symbol: '₺', bg: '#fef3c7', color: '#b45309' },
    USD: { symbol: '$', bg: '#dcfce7', color: '#15803d' },
    EUR: { symbol: '€', bg: '#dbeafe', color: '#1d4ed8' },
    GBP: { symbol: '£', bg: '#ede9fe', color: '#6d28d9' },
    CHF: { symbol: '₣', bg: '#fee2e2', color: '#b91c1c' },
    JPY: { symbol: '¥', bg: '#ffedd5', color: '#c2410c' },
    CNY: { symbol: '¥', bg: '#fce7f3', color: '#9d174d' },
    RUB: { symbol: '₽', bg: '#e0f2fe', color: '#0369a1' },
    AED: { symbol: 'د.إ', bg: '#f0fdf4', color: '#166534' },
};

const getCurrencyMeta = (code: string) => {
    const upper = code?.toUpperCase();
    return currencyMeta[upper] ?? { symbol: code?.charAt(0) ?? '?', bg: '#f1f5f9', color: '#475569' };
};

const ParaBirimleri: React.FC = () => {
    const { t } = useTranslation();
    const [paraBirimleri, setParaBirimleri] = useState<ParaBirimi[]>([]);
    const [selectedParaBirimi, setSelectedParaBirimi] = useState<ParaBirimi | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState<ParaBirimiForm>({ birim: '', carpan: 1 });

    const fetchParaBirimleri = async () => {
        try {
            const response = await axios.get('/api/ParaBirimleri');
            setParaBirimleri(response.data);
        } catch {
            toast.error(t('msg.islemHatasi'));
        }
    };

    useEffect(() => { fetchParaBirimleri(); }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (selectedParaBirimi) {
                await axios.put(`/api/ParaBirimleri/${selectedParaBirimi.paraBirimiID}`, formData);
                toast.success(t('msg.islemBasarili'));
            } else {
                await axios.post('/api/ParaBirimleri', formData);
                toast.success(t('msg.islemBasarili'));
            }
            fetchParaBirimleri();
            handleCloseModal();
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (pb: ParaBirimi) => {
        setSelectedParaBirimi(pb);
        setFormData({ birim: pb.birim, carpan: pb.carpan });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedParaBirimi(null);
        setFormData({ birim: '', carpan: 1 });
    };

    // TRY/TL is the base currency (carpan ≈ 1)
    const baseCarpan = paraBirimleri.find(p =>
        p.birim.toUpperCase() === 'TRY' || p.birim.toUpperCase() === 'TL'
    )?.carpan ?? 1;

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

                .pb-page {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    padding: 28px 32px;
                    background: #f4f6fb;
                    min-height: 100vh;
                }

                /* ── Header ── */
                .pb-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 28px;
                }
                .pb-header-left { display: flex; flex-direction: column; gap: 3px; }
                .pb-title {
                    font-size: 22px;
                    font-weight: 700;
                    color: #0f172a;
                    letter-spacing: -0.4px;
                    margin: 0;
                }
                .pb-subtitle { font-size: 13px; color: #94a3b8; font-weight: 400; }
                .pb-add-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 20px;
                    background: #cd6906;
                    color: #fff;
                    border: none;
                    border-radius: 10px;
                    font-size: 14px;
                    font-weight: 600;
                    font-family: inherit;
                    cursor: pointer;
                    box-shadow: 0 4px 14px rgba(205,105,6,0.35);
                    transition: background 0.18s, box-shadow 0.18s, transform 0.12s;
                }
                .pb-add-btn:hover {
                    background: #b85d05;
                    box-shadow: 0 6px 20px rgba(205,105,6,0.45);
                    transform: translateY(-1px);
                }
                .pb-add-btn svg { width: 16px; height: 16px; stroke: #fff; stroke-width: 2.5; fill: none; }

                /* ── Card ── */
                .pb-card {
                    background: #fff;
                    border-radius: 14px;
                    box-shadow: 0 1px 4px rgba(15,23,42,0.07), 0 4px 24px rgba(15,23,42,0.05);
                    overflow: visible;
                }

                /* ── Table ── */
                .pb-table { width: 100%; border-collapse: collapse; }
                .pb-table thead tr { border-bottom: 1.5px solid #f1f5f9; }
                .pb-table thead th {
                    padding: 13px 20px;
                    font-size: 11px;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                    color: #94a3b8;
                    text-align: left;
                    white-space: nowrap;
                }
                .pb-table thead th.pb-th-r { text-align: right; }
                .pb-table thead th.pb-th-actions { text-align: right; }
                .pb-table tbody tr {
                    border-bottom: 1px solid #f8fafc;
                    transition: background 0.13s;
                }
                .pb-table tbody tr:last-child { border-bottom: none; }
                .pb-table tbody tr.pb-row-hovered { background: #f0f5ff; }
                .pb-table tbody td {
                    padding: 15px 20px;
                    font-size: 14px;
                    color: #1e293b;
                    vertical-align: middle;
                }

                /* Cell variants */
                .pb-cell-id { font-size: 12px; color: #94a3b8; font-weight: 500; width: 56px; }
                .pb-cell-birim { display: flex; align-items: center; gap: 12px; }
                .pb-currency-badge {
                    width: 38px; height: 38px;
                    border-radius: 10px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 15px; font-weight: 700;
                    flex-shrink: 0;
                    letter-spacing: -0.5px;
                }
                .pb-birim-name { font-weight: 700; color: #0f172a; font-size: 15px; letter-spacing: 0.5px; }
                .pb-birim-sub { font-size: 11px; color: #94a3b8; font-weight: 400; margin-top: 1px; }

                /* Carpan cell */
                .pb-cell-carpan { text-align: right; }
                .pb-carpan-wrap {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    justify-content: flex-end;
                }
                .pb-carpan-val {
                    font-family: 'SF Mono', 'Fira Mono', 'Cascadia Code', 'Consolas', monospace;
                    font-size: 14px;
                    font-weight: 600;
                    color: #0f172a;
                    letter-spacing: -0.3px;
                }
                .pb-carpan-base {
                    font-size: 11px;
                    color: #94a3b8;
                    font-weight: 400;
                    white-space: nowrap;
                }

                /* Rate bar (visual multiplier indicator) */
                .pb-rate-bar-wrap { display: flex; align-items: center; gap: 8px; flex-direction: column; align-items: flex-end; }
                .pb-rate-bar-track {
                    width: 80px; height: 4px;
                    background: #f1f5f9; border-radius: 2px; overflow: hidden;
                }
                .pb-rate-bar-fill {
                    height: 100%; border-radius: 2px;
                    background: linear-gradient(90deg, #e87d0d, #cd6906);
                    max-width: 100%;
                }

                /* Ghost buttons */
                .pb-actions {
                    display: flex; justify-content: flex-end; gap: 6px;
                    opacity: 0; transition: opacity 0.15s;
                }
                .pb-row-hovered .pb-actions { opacity: 1; }
                .pb-ghost-btn {
                    display: inline-flex; align-items: center; justify-content: center;
                    width: 34px; height: 34px;
                    border-radius: 8px; border: 1.5px solid transparent;
                    background: transparent; cursor: pointer;
                    transition: background 0.15s, border-color 0.15s;
                    color: #64748b;
                }
                .pb-ghost-edit:hover { background: #fff8f0; border-color: #f5d5a0; color: #cd6906; }
                .pb-ghost-delete:hover { background: #fff1f2; border-color: #fecdd3; color: #e11d48; }
                .pb-ghost-btn svg {
                    width: 15px; height: 15px;
                    stroke: currentColor; stroke-width: 2;
                    fill: none; stroke-linecap: round; stroke-linejoin: round;
                }
                .pb-cell-actions { width: 90px; text-align: right; }

                /* Footer */
                .pb-footer {
                    padding: 13px 20px; border-top: 1.5px solid #f1f5f9;
                    display: flex; align-items: center; justify-content: space-between;
                    border-radius: 0 0 14px 14px;
                }
                .pb-count { font-size: 13px; color: #94a3b8; font-weight: 500; }
                .pb-count span { color: #475569; font-weight: 600; }

                /* Empty */
                .pb-empty { text-align: center; padding: 56px 20px; color: #94a3b8; }
                .pb-empty-icon { font-size: 36px; margin-bottom: 10px; opacity: 0.5; }
                .pb-empty-text { font-size: 15px; font-weight: 500; }

                /* ── Modal ── */
                .pb
                .pb-modal {
                    background: #fff;
                    border-radius: 16px 0 0 16px;
                    width: 480px; max-width: 92vw;
                    height: 100%;
                    box-shadow: -8px 0 40px rgba(0,0,0,0.12);
                    animation: pbDrawerIn 0.28s cubic-bezier(0.32,0.72,0,1);
                    display: flex; flex-direction: column;
                    overflow: hidden;
                }
                @keyframes pbDrawerIn {
                    from { transform: translateX(100%); }
                    to   { transform: translateX(0); }
                }
                    to   { opacity: 1; transform: translateY(0) scale(1); }
                }
                .pb-modal-header {
                    padding: 20px 24px; border-bottom: 1px solid #f3f4f6;
                    flex-shrink: 0;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .pb-modal-title { font-size: 18px; font-weight: 600; color: #0f172a; margin: 0; }
                .pb-modal-close {
                    width: 32px; height: 32px; border-radius: 8px; border: none;
                    background: none; color: #9ca3af; cursor: pointer;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 16px; transition: background 0.15s;
                }
                .pb-modal-close:hover { background: #f3f4f6; color: #374151; }
                .pb-modal-body { padding: 24px; display: flex; flex-direction: column; gap: 20px; flex: 1; overflow-y: auto; }
                .pb-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
                .pb-field label {
                    display: block;
                    font-size: 12px; font-weight: 600;
                    letter-spacing: 0.5px;
                    color: #64748b; margin-bottom: 6px;
                }
                .pb-field input {
                    width: 100%; padding: 10px 14px;
                    border: 1.5px solid #e2e8f0; border-radius: 9px;
                    font-size: 14px; font-family: inherit; color: #0f172a;
                    outline: none;
                    transition: border-color 0.15s, box-shadow 0.15s;
                    box-sizing: border-box;
                }
                .pb-field input:focus {
                    border-color: #cd6906;
                    box-shadow: 0 0 0 3px rgba(205,105,6,0.12);
                }
                .pb-field input[type="number"] {
                    font-family: 'SF Mono', 'Fira Mono', 'Consolas', monospace;
                }
                .pb-field-hint { font-size: 11px; color: #94a3b8; margin-top: 5px; }
                .pb-modal-footer {
                    padding: 16px 24px; border-top: 1px solid #f3f4f6;
                    background: rgba(255,255,255,0.9); backdrop-filter: blur(8px); flex-shrink: 0;
                    display: flex; justify-content: flex-end; gap: 10px;
                }
                .pb-btn-cancel {
                    padding: 9px 18px; border-radius: 8px; border: none;
                    background: none; color: #4b5563;
                    font-size: 13px; font-weight: 500; font-family: inherit;
                    cursor: pointer; transition: background 0.15s;
                }
                .pb-btn-cancel:hover { background: #f9fafb; }
                .pb-btn-save {
                    padding: 9px 22px; border-radius: 9px; border: none;
                    background: #cd6906; color: #fff;
                    font-size: 13px; font-weight: 600; font-family: inherit;
                    cursor: pointer;
                    display: inline-flex; align-items: center; gap: 6px;
                    box-shadow: 0 2px 8px rgba(205,105,6,0.3);
                    transition: background 0.15s, transform 0.1s;
                }
                .pb-btn-save:hover:not(:disabled) { background: #b85d05; transform: translateY(-1px); }
                .pb-btn-save:disabled { opacity: 0.65; cursor: not-allowed; }
                .pb-spinner {
                    width: 14px; height: 14px;
                    border: 2px solid rgba(255,255,255,0.4);
                    border-top-color: #fff; border-radius: 50%;
                    animation: pbSpin 0.7s linear infinite;
                }
                @keyframes pbSpin { to { transform: rotate(360deg); } }
            `}</style>

            <div style={{ fontFamily: "'Inter', -apple-system, sans-serif", padding: '24px', background: '#fff' }}>
                {/* Header */}
                <div style={{ paddingBottom: 20, marginBottom: 24, borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <h1 style={{ fontSize: 20, fontWeight: 600, color: '#111827', margin: 0, lineHeight: 1.3 }}>Para Birimleri</h1>
                        <p style={{ fontSize: 13, color: '#6b7280', margin: '3px 0 0' }}>{t('ayarlar.paraBirimi.aciklama')}</p>
                    </div>
                    <button
                        onClick={() => setShowModal(true)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#cd6906', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', boxShadow: '0 1px 3px rgba(205,105,6,0.3)', transition: 'all 0.15s' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#a85505'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#cd6906'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; }}
                    >
                        <svg style={{ width: 14, height: 14, stroke: '#fff', strokeWidth: 2.5, fill: 'none' }} viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        Yeni Para Birimi
                    </button>
                </div>

                {/* Card List */}
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
                    {paraBirimleri.length === 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 20px', color: '#9ca3af' }}>
                            <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}>💱</div>
                            <p style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>{t('msg.henuzKayitEklenmedi')}</p>
                        </div>
                    ) : (
                        paraBirimleri.map((pb, idx) => {
                            const meta = getCurrencyMeta(pb.birim);
                            return (
                                <div
                                    key={pb.paraBirimiID}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: idx < paraBirimleri.length - 1 ? '1px solid #f3f4f6' : 'none', transition: 'background 0.13s' }}
                                    onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <div style={{ width: 38, height: 38, borderRadius: 10, background: meta.bg, color: meta.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, flexShrink: 0 }}>
                                            {meta.symbol}
                                        </div>
                                        <div>
                                            <p style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>{pb.birim.toUpperCase()}</p>
                                            <p style={{ fontSize: 12, color: '#6b7280', margin: '2px 0 0' }}>
                                                {pb.carpan.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} ₺ / 1 {pb.birim.toUpperCase()}
                                            </p>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        <button title={t('btn.duzenle')} onClick={() => handleEdit(pb)}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: '#2563eb', display: 'flex', transition: 'background 0.13s' }}
                                            onMouseEnter={e => (e.currentTarget.style.background = '#eff6ff')}
                                            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                                        >
                                            <svg style={{ width: 15, height: 15, stroke: 'currentColor', strokeWidth: 2, fill: 'none' }} viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                        </button>
                                        <button title={t('btn.sil')} onClick={() => toast.info(t('msg.onayGerekli'))}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: '#f43f5e', display: 'flex', transition: 'background 0.13s' }}
                                            onMouseEnter={e => (e.currentTarget.style.background = '#fff1f2')}
                                            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                                        >
                                            <svg style={{ width: 15, height: 15, stroke: 'currentColor', strokeWidth: 2, fill: 'none' }} viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="pb-overlay" onClick={handleCloseModal}>
                    <div className="pb-modal" onClick={e => e.stopPropagation()}>
                        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                            <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827', margin: 0 }}>{selectedParaBirimi ? 'Para Birimi Düzenle' : 'Yeni Para Birimi Ekle'}</h2>
                            <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', borderRadius: 8, color: '#9ca3af', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32 }} onClick={handleCloseModal}>✕</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="pb-modal-body">
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Para Birimi Kodu</label>
                                        <input
                                            type="text"
                                            id="birimInput"
                                            placeholder="USD, EUR, TRY..."
                                            value={formData.birim}
                                            onChange={e => setFormData({ ...formData, birim: e.target.value.toUpperCase() })}
                                            required
                                            maxLength={10}
                                            autoFocus
                                            style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', color: '#111827', outline: 'none', boxSizing: 'border-box' as const, transition: 'border-color 0.15s, box-shadow 0.15s' }}
                                            onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                            onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Çarpan (Kur)</label>
                                        <input
                                            type="number"
                                            id="carpanInput"
                                            placeholder="1.0000"
                                            value={formData.carpan}
                                            onChange={e => setFormData({ ...formData, carpan: parseFloat(e.target.value) || 0 })}
                                            required
                                            step="0.0001"
                                            min="0.0001"
                                            style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', color: '#111827', outline: 'none', boxSizing: 'border-box' as const, transition: 'border-color 0.15s, box-shadow 0.15s' }}
                                            onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                            onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                        />
                                    </div>
                                </div>
                                <p className="pb-field-hint">
                                    Çarpan: 1 birim dövizin Türk Lirası karşılığıdır. Örn: 1 USD = 41.80 ₺ için <strong>41.8</strong> girin.
                                </p>
                            </div>
                            <div style={{ padding: '16px 24px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end', gap: 10, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)', flexShrink: 0 }}>
                                <button
                                    type="button"
                                    style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' }}
                                    onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                                    onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                                    onClick={handleCloseModal}
                                >{t('btn.iptal')}</button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: '#cd6906', color: '#fff', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: saving ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7, boxShadow: '0 1px 3px rgba(205,105,6,0.3)', opacity: saving ? 0.75 : 1 }}
                                    onMouseEnter={e => { if (!saving) { e.currentTarget.style.background = '#a85505'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; }}}
                                    onMouseLeave={e => { e.currentTarget.style.background = '#cd6906'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; }}
                                >
                                    {saving && <span className="pb-spinner" />}
                                    {selectedParaBirimi ? t('btn.kaydet') : t('btn.ekle')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default ParaBirimleri;